import React, { useState } from 'react';
import {
  Navigate,
  useParams,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import { StyleSheet, Text, View } from './components/base_components';
import { useBusy } from './components/busy_component';
import BreadcrumbPath from './components/breadcrumb_path';
import ProgressSpinner from './components/progress_spinner';
import StandardBoxList from './components/standard_box_list';
import TextButton from './components/buttons/text_button';
import TitleActionLine from './components/title_action_line';
import ActionProgress from './components/action_progress';

import VolumeDirHelper from './lib/volume_dir_helper';

import MediaGrantStore from './stores/media_grant_store';
import MediaStore from './stores/media_store';
import UserStore from './stores/user_store';

import { byteFormat } from './tools/util';

const styles = StyleSheet.create({
  bulkDeleteScreen: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
  },
  actionButton: { marginLeft: '1rem' },
  spinner: { margin: '1.5rem', alignSelf: 'center' },
  text: {
    marginTop: '2rem',
    alignSelf: 'center',
    color: '#18182d',
    fontSize: '1.5rem',
    fontWeight: '400',
  },
  progress: { margin: '2rem' },
});

export default function BulkDeleteScreen(props) {
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const org = UserStore.useOrg();
  const grant_name = params.grant_name ?? '';
  const path = '/' + (params['*'] ?? '');
  const grant = MediaGrantStore.useMediaGrant(org?.org_id, grant_name);
  const media_grant_id = grant?.media_grant_id;
  const [list, setList] = React.useState<any[]>([]);
  const [is_ready, setIsReady] = React.useState(false);
  const [is_busy, setBusy, clearBusy] = useBusy(false);
  const [selected, setSelected] = React.useState([]);
  const [current, setCurrent] = React.useState(0);
  const [is_failed, setIsFailed] = useState(false);
  const [is_done, setIsDone] = useState(false);

  React.useEffect(() => {
    if (location?.state && media_grant_id && org) {
      const { media_list, dir_list } = location.state;
      VolumeDirHelper.fetch(
        { org_id: org?.org_id, media_grant_id, path, media_list, dir_list },
        (err, found_list) => {
          if (err) {
            window.alert('Failed to collect files, please try again.');
          } else {
            setIsReady(true);
            setList(found_list);
            setSelected(found_list.map((row, i) => i));
          }
        }
      );
    }
  }, [org, media_grant_id, path, location?.state]);

  function _onDeleteProgress({ count, total }) {
    setCurrent(current);
  }
  function _onClosePress() {
    navigate(-1);
  }

  function _onDeletePress() {
    if (selected?.length > 0) {
      const yes = window.confirm(
        `Are you sure you want to delete ${selected.length} items?`
      );
      if (yes && setBusy()) {
        setCurrent(0);
        const media_list = selected.map((i) => list[i]);
        MediaStore.deleteList(
          {
            org_id: org.org_id,
            media_grant_id,
            media_list,
            on_progress: _onDeleteProgress,
          },
          (err) => {
            if (err) {
              window.alert('Delete failed, please try again.');
              setIsFailed(true);
            } else {
              setCurrent(selected.length);
            }
            setIsDone(true);
            clearBusy();
          }
        );
      }
    }
  }
  let content: any = null;
  if (is_ready) {
    const prefix_len = path.length;
    const selected_list = selected.map((i) => list[i]);
    const total_size = selected_list.reduce(
      (memo, file) => memo + file.file_size,
      0
    );
    const show_progress = is_busy || is_done || is_failed;
    const rows = list.map((file) => {
      return [file.media_path.slice(prefix_len), byteFormat(file.file_size)];
    });
    const buttons = (
      <>
        <TextButton
          style={styles.actionButton}
          disabled={show_progress}
          text='Delete All'
          onPress={_onDeletePress}
        />
      </>
    );
    content = (
      <>
        {show_progress ? (
          <ActionProgress
            style={styles.progress}
            action='Delete'
            total={selected.length}
            current={current}
            running={is_busy}
            failed={is_failed}
            onRetryPress={_onDeletePress}
            onClosePress={_onClosePress}
          />
        ) : null}
        <StandardBoxList
          title='Delete Files'
          detail={
            rows
              ? `${selected_list.length} Files, ${byteFormat(total_size)}`
              : ''
          }
          buttons={buttons}
          selected={selected}
          onChange={setSelected}
          disabled={show_progress}
          headers={['Path', 'Size']}
          rows={rows}
          sizes={[3]}
        />
      </>
    );
  } else {
    content = (
      <>
        <TitleActionLine title='Bulk Delete Files' />
        <Text style={styles.text}>Collecting directory entries...</Text>
        <ProgressSpinner style={styles.spinner} color='#999' />
      </>
    );
  }

  return location.state ? (
    <View style={styles.bulkDeleteScreen}>
      <BreadcrumbPath
        volumeName={grant?.grant_name}
        prefix={`/home/${grant?.grant_name}/`}
        path={path}
        extra='Bulk Delete'
      />
      {content}
    </View>
  ) : (
    <Navigate to={`/home/${grant_name}${path}`} />
  );
}
